import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";

const Composer = ({ groups, onSent }) => {
  const [f, setF] = useState({ artworkSlug: "", subject: "New work from the studio", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const audience = f.artworkSlug ? groups.find((g) => g.slug === f.artworkSlug) : null;
  const count = audience ? audience.count : new Set(groups.flatMap((g) => g.emails)).size;

  const send = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Send this email to ${count} ${count === 1 ? "person" : "people"}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/waitlist/email", { ...f, artworkSlug: f.artworkSlug || null });
      toast.success(`Sent to ${data.sent}${data.failed ? ` · ${data.failed} failed` : ""}`);
      set("message", "");
      onSent();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={send} className="space-y-4 bg-[#FDFBF7] p-6" data-testid="waitlist-composer">
      <p className="eyebrow">Email a waitlist</p>
      <label className="block"><span className="field-label mb-1">Send to</span>
        <select className="admin-input" value={f.artworkSlug} onChange={(e) => set("artworkSlug", e.target.value)} data-testid="waitlist-audience-select">
          <option value="">Everyone on any waitlist</option>
          {groups.map((g) => <option key={g.slug} value={g.slug}>{g.title} ({g.count})</option>)}
        </select>
      </label>
      <label className="block"><span className="field-label mb-1">Subject</span><input required className="admin-input" value={f.subject} onChange={(e) => set("subject", e.target.value)} data-testid="waitlist-subject-input" /></label>
      <label className="block"><span className="field-label mb-1">Message</span><textarea required rows={6} className="admin-input" value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="A new painting in the same series is finished…" data-testid="waitlist-message-input" /></label>
      <p className="text-xs text-[#8A8780]">Each person gets their own email with a link to the collection and a link to leave the waitlist.</p>
      <button className="btn-primary" disabled={busy || count === 0} data-testid="waitlist-send-btn">{busy ? "Sending…" : `Send to ${count}`}</button>
    </form>
  );
};

export const WaitlistTab = () => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/waitlist").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
  }, [load]);
  const groups = useMemo(() => {
    const m = {};
    items.forEach((i) => {
      const g = (m[i.artworkSlug] ||= { slug: i.artworkSlug, title: i.artworkTitle, count: 0, emails: [] });
      g.count += 1;
      g.emails.push(i.email);
    });
    return Object.values(m);
  }, [items]);
  const remove = async (i) => {
    if (!window.confirm(`Remove ${i.email} from the "${i.artworkTitle}" waitlist?`)) return;
    await api.delete(`/admin/waitlist/${i.id}`).then(load).catch((e) => toast.error(errMsg(e)));
  };

  return (
    <div className="grid gap-8 lg:grid-cols-12" data-testid="admin-waitlist">
      <div className="bg-[#FDFBF7] p-6 lg:col-span-7">
        <p className="eyebrow mb-6" data-testid="waitlist-count">{items.length} {items.length === 1 ? "person" : "people"} waiting</p>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#E5E0D8] text-left text-[10px] uppercase tracking-[0.2em] text-[#5A626A]"><th className="py-3">Email</th><th>Waiting for</th><th>Joined</th><th>Last emailed</th><th /></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="py-6 text-[#8A8780]">No one on the waitlist yet</td></tr>}
            {items.map((i) => (
              <tr key={i.id} className="border-b border-[#F0EDE6]" data-testid={`waitlist-row-${i.id}`}>
                <td className="py-3">{i.email}</td><td>{i.artworkTitle}</td><td>{fmtDate(i.createdAt)}</td><td>{fmtDate(i.notifiedAt)}</td>
                <td className="text-right"><button className="text-xs text-[#8B2E24] underline underline-offset-4" onClick={() => remove(i)} data-testid={`waitlist-remove-${i.id}`}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:col-span-5"><Composer groups={groups} onSent={load} /></div>
    </div>
  );
};
